/*******************************************************************************
 * Copyright 2026 Collaterate. All rights reserved.
 *******************************************************************************/

// Storefront AI answer for [[ semantic_search_answer ]].
//
// SAME-ORIGIN at config.restBase + '/search/answer', never the engine directly:
// its CORS allowlist is deployed config and every site has its own domain. The
// proxy attaches the context token server-side, so there is no token here and
// there must not be one -- that would be a bearer credential in page source.
//
// Runs AFTER load, not during render: /answers fronts Bedrock with a 60-second
// budget, so the bones ships a hidden container and this fills it once the page
// is up. No supersession guard needed -- no typing, so one request per load.
//
// The answer is grounded on the page's applied f.* filters and remembered per term
// AND filters for the tab, so a narrowed page gets an answer about what it shows,
// and returning to the same term and filters replays it instead of re-asking.
//
// Raises exactly one event per load: SEARCH_ANSWER_LOADED, or
// SEARCH_ANSWER_UNAVAILABLE on every path that ends without prose. A pair,
// because a theme drawing its own treatment cannot wait for an absence, and ONE
// unavailable event because the endpoint deliberately collapses no_candidates,
// model_unavailable, a timeout and a transport failure into the same result.
//
// EVERY path that does not end in prose leaves the container HIDDEN: an empty
// block under an AI label reads as broken, an absent one as a site without it.
//
// ES5 on purpose, matching the rest of this bundle's browser targets.

(function (window, document) {
    'use strict';

    // Above the proxy's own 65s Feign ceiling, so the server's answer -- including its fail-soft "no
    // answer" -- arrives rather than being cut off here. This exists for a hung connection, which
    // would otherwise leave the container aria-busy for the life of the page.
    var TIMEOUT_MS = 70000;

    function config() {
        return window.searchAnswerConfig || null;
    }

    function logSuppressed(error) {
        try {
            if (window.console && window.console.debug) {
                window.console.debug('searchAnswer suppressed an error', error);
            }
        } catch (ignored) {
            // Nothing further to do: a logging failure must not become a page failure.
        }
    }

    function init() {
        var cfg = config();
        if (!cfg) {
            // No config global means the tag is not on this page. Not an error.
            return;
        }
        var container = document.getElementById(cfg.containerId);
        if (!container) {
            return;
        }
        var body = container.querySelector('.c-search-answer-body');
        if (!body) {
            // A theme override removed the body element. Nothing to fill, so do not announce a
            // container that will never update.
            return;
        }

        // The term the SERVER rendered, not one re-derived from the URL: two implementations of "what
        // did the shopper search for" are free to disagree, and the grid above uses the server's.
        var term = container.getAttribute('data-search-term');
        if (!term || !term.replace(/^\s+|\s+$/g, '')) {
            // The search template is reachable with an empty k, and answering nothing is a Bedrock call.
            return;
        }

        // Keyed by IDENTITY, limit, term and the applied filters (see filterParams below). sessionStorage is per-origin and every site has its own
        // domain, so the site needs no place in the key. The identity does: /search/answer is not
        // identity-neutral, since the token the proxy mints carries the segment visibility that
        // filters the grounding set, and sessionStorage survives login, logout and account switch in
        // the same tab. Without it a shopper who searched anonymously and then signed in was served
        // the anonymous answer for the rest of the TTL, and the reverse leaked a segment-restricted
        // answer to an anonymous view. The limit is in the key because it changes the grounding set
        // and so the prose, and the filters for the same reason. Tab-scoped rather than localStorage: an answer outliving the visit would
        // be served against a catalog that has since changed.
        var CACHE_PREFIX = 'collaterate:searchAnswer:';
        var CACHE_TTL_MS = 1800000;

        // The applied facet filters travel with the question, so the answer is grounded on the results the
        // shopper is looking at rather than describing every match for the term. Read from the query the SERVER
        // settled on (data-canonical-query, which carries the values the interpreter applied from the words)
        // when the page has one, else from the address bar. Only f.* pairs, kept as encoded, sorted so the same
        // filters in a different order are one cache entry. Also in the cache key: a narrowed page must never be
        // served the unfiltered answer, which is the bug this fixes.
        function filterParams() {
            var query = '';
            try {
                var holder = document.querySelector('[data-canonical-query]');
                query = (holder && holder.getAttribute('data-canonical-query'))
                    || (window.location && window.location.search) || '';
            } catch (error) {
                // No filters is the unfiltered answer, which is what the page had before this existed.
                logSuppressed(error);
            }
            var pairs = query.replace(/^\?/, '').split('&');
            var kept = [];
            for (var i = 0; i < pairs.length; i++) {
                var key = pairs[i].split('=')[0];
                var decoded = key;
                try {
                    decoded = window.decodeURIComponent(key);
                } catch (ignored) {
                    // An undecodable key is compared as written.
                }
                if (decoded.indexOf('f.') === 0 && pairs[i].indexOf('=') > 0) {
                    kept.push(pairs[i]);
                }
            }
            kept.sort();
            return kept.join('&');
        }
        var filters = filterParams();

        function cacheKey() {
            var globals = window.productDiscoveryGlobals || {};
            return CACHE_PREFIX + (globals.identityKey || '') + ':' + (cfg.limit || 6) + ':' + term
                + (filters ? '|' + filters : '');
        }

        function readCached() {
            try {
                var raw = window.sessionStorage.getItem(cacheKey());
                if (!raw) {
                    return null;
                }
                var entry = window.JSON.parse(raw);
                if (!entry || typeof entry.t !== 'number' || (Date.now() - entry.t) > CACHE_TTL_MS) {
                    return null;
                }
                return entry;
            } catch (error) {
                // Private mode, site data disabled, or unparseable. Fall through and ask.
                return null;
            }
        }

        // Only ever called for a 2xx. A timeout, a non-2xx and a bad body are transient, and caching
        // one would pin an outage to this term for the life of the tab.
        function writeCached(prose) {
            try {
                window.sessionStorage.setItem(cacheKey(), window.JSON.stringify({ answer: prose, t: Date.now() }));
            } catch (error) {
                logSuppressed(error);
            }
        }

        // Exactly one of the pair, exactly once: a timeout drives ontimeout AND then readyState 4 with
        // status 0, so both giveUp() paths run for a single failure.
        var announced = false;

        function announce(constantName) {
            if (announced) {
                return;
            }
            announced = true;
            try {
                var bus = window.events;
                // Guard on the constant, not just on the bus: trigger() is gated on isEventRegistered,
                // so a name templateHeader.js never declared is a silent no-op.
                if (bus && bus[constantName]) {
                    bus.trigger(bus[constantName]);
                }
            } catch (error) {
                // A theme's listener throwing must not break the search page.
                logSuppressed(error);
            }
        }

        function setBusy(busy) {
            try {
                container.setAttribute('aria-busy', busy ? 'true' : 'false');
                if (busy) {
                    container.classList.add('is-loading');
                } else {
                    container.classList.remove('is-loading');
                }
            } catch (error) {
                logSuppressed(error);
            }
        }

        // One exit for every non-answer. A container left aria-busy tells assistive tech to wait for
        // an update that is never coming, so clearing it belongs here rather than on the success path.
        function giveUp() {
            setBusy(false);
            container.hidden = true;
            announce('SEARCH_ANSWER_UNAVAILABLE');
        }

        function reveal(prose) {
            // UNHIDE FIRST, then fill: an aria-live region only announces mutations to content already
            // in the accessibility tree, and a hidden element is not. Both run in one task, so no frame
            // shows an empty block. Improves the odds rather than guaranteeing them -- a region visible
            // from the start would put an AI label over nothing on every search with no answer.
            container.hidden = false;
            // textContent, never innerHTML: this is model output relayed through the engine, which
            // does not redact ungrounded content, so treat the string as arbitrary.
            body.textContent = prose;
            setBusy(false);
            // After the prose is in the DOM: a listener will measure or restyle the block.
            announce('SEARCH_ANSWER_LOADED');
        }

        var cached = readCached();
        if (cached) {
            // Straight to the same two exits the live path uses, so the event pair still fires: a
            // theme listening for SEARCH_ANSWER_LOADED must hear it on a facet navigation too.
            // No setBusy(true) first -- there is no wait to announce.
            var cachedProse = cached.answer;
            if (typeof cachedProse === 'string' && cachedProse.replace(/^\s+|\s+$/g, '')) {
                reveal(cachedProse);
            } else {
                giveUp();
            }
            return;
        }

        setBusy(true);
        try {
            var xhr = new window.XMLHttpRequest();
            var url = cfg.restBase + '/search/answer?q=' + window.encodeURIComponent(term)
                + '&limit=' + window.encodeURIComponent(cfg.limit || 6)
                + (filters ? '&' + filters : '');
            xhr.open('GET', url, true);
            xhr.withCredentials = true;
            xhr.timeout = TIMEOUT_MS;
            xhr.ontimeout = function () {
                giveUp();
            };
            xhr.onreadystatechange = function () {
                if (xhr.readyState !== 4) {
                    return;
                }
                try {
                    if (xhr.status < 200 || xhr.status >= 300) {
                        giveUp();
                        return;
                    }
                    var parsed = window.JSON.parse(xhr.responseText);
                    // parsed.content, NOT parsed.data: WrapperDTO serializes as
                    // {messages, errors, content}. See RestEnvelopeContractTest.
                    var payload = (parsed && parsed.content) || null;
                    var prose = payload && payload.answer;
                    // A null answer is a successful response, not an error: the engine returns null for
                    // both no_candidates and model_unavailable.
                    if (typeof prose !== 'string' || !prose.replace(/^\s+|\s+$/g, '')) {
                        // Remembered too. A null answer is a settled result for this term, and
                        // re-asking on the next facet click is the same Bedrock call for the same null.
                        writeCached(null);
                        giveUp();
                        return;
                    }
                    writeCached(prose);
                    reveal(prose);
                } catch (parseError) {
                    // A 2xx carrying something that is not JSON: an ALB or WAF error page served as 200.
                    giveUp();
                    logSuppressed(parseError);
                }
            };
            xhr.send();
        } catch (sendError) {
            giveUp();
            logSuppressed(sendError);
        }
    }

    function safeInit() {
        try {
            init();
        } catch (error) {
            // A search results page must never break because the answer block could not be filled.
            logSuppressed(error);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', safeInit);
    } else {
        safeInit();
    }
}(window, document));
